import {
  AppstoreOutlined,
  AuditOutlined,
  BarChartOutlined,
  CarOutlined,
  LogoutOutlined,
  MenuOutlined,
  PrinterOutlined,
  SettingOutlined,
  ShoppingCartOutlined,
  SyncOutlined,
  TeamOutlined,
  TruckOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import { hasPermission, type Permission } from '@riba/shared';
import { Button, Drawer, Dropdown, Layout, Menu, Select, Space, Spin, Typography } from 'antd';
import { Suspense, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation, useNavigate } from 'react-router';
import { useAuthStore } from '../auth/auth.store';
import { useIsMobile } from '../components/useIsMobile';
import { LOCALES, setLocale, type Locale } from '../i18n';

interface NavItem {
  key: string;
  labelKey: string;
  icon: React.ReactNode;
  permission: Permission;
}

/**
 * Navigation is filtered by the shared RBAC map, so a picker who somehow lands
 * here sees nothing they cannot use — and the API would refuse them anyway.
 */
const NAV: NavItem[] = [
  { key: '/clients', labelKey: 'nav.clients', icon: <TeamOutlined />, permission: 'clients:read' },
  {
    key: '/orders',
    labelKey: 'nav.orders',
    icon: <ShoppingCartOutlined />,
    permission: 'orders:read:all',
  },
  {
    key: '/catalog',
    labelKey: 'nav.catalog',
    icon: <UnorderedListOutlined />,
    permission: 'catalog:read:admin',
  },
  {
    key: '/warehouse-map',
    labelKey: 'nav.warehouseMap',
    icon: <AppstoreOutlined />,
    permission: 'catalog:read:admin',
  },
  {
    key: '/labels',
    labelKey: 'nav.labels',
    icon: <PrinterOutlined />,
    permission: 'catalog:read:admin',
  },
  { key: '/staff', labelKey: 'nav.staff', icon: <CarOutlined />, permission: 'staff:manage' },
  {
    key: '/shipments',
    labelKey: 'nav.shipments',
    icon: <TruckOutlined />,
    permission: 'shipments:read:all',
  },
  {
    key: '/reports',
    labelKey: 'nav.reports',
    icon: <BarChartOutlined />,
    permission: 'reports:view',
  },
  { key: '/sync', labelKey: 'nav.sync', icon: <SyncOutlined />, permission: 'sync:manage' },
  {
    key: '/settings',
    labelKey: 'nav.settings',
    icon: <SettingOutlined />,
    permission: 'settings:manage',
  },
  { key: '/audit', labelKey: 'nav.audit', icon: <AuditOutlined />, permission: 'audit:read' },
];

export function AppLayout() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const user = useAuthStore((s) => s.user);
  const clear = useAuthStore((s) => s.clear);
  const isMobile = useIsMobile();
  const [navOpen, setNavOpen] = useState(false);

  const items = NAV.filter((item) => hasPermission(user, item.permission)).map((item) => ({
    key: item.key,
    icon: item.icon,
    label: t(item.labelKey),
  }));

  const nav = (
    <>
      <div
        style={{
          height: 56,
          display: 'flex',
          alignItems: 'center',
          padding: '0 20px',
          fontWeight: 600,
          borderBottom: '1px solid #f0f0f0',
        }}
      >
        {t('app.title')}
      </div>
      <Menu
        mode="inline"
        selectedKeys={[`/${location.pathname.split('/')[1] ?? ''}`]}
        items={items}
        onClick={({ key }) => {
          setNavOpen(false);
          void navigate(key);
        }}
        style={{ borderInlineEnd: 'none' }}
      />
    </>
  );

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {isMobile ? (
        <Drawer
          placement="left"
          width={260}
          open={navOpen}
          onClose={() => setNavOpen(false)}
          closable={false}
          styles={{ body: { padding: 0 } }}
        >
          {nav}
        </Drawer>
      ) : (
        <Layout.Sider breakpoint="lg" collapsedWidth="0" width={232} theme="light">
          {nav}
        </Layout.Sider>
      )}

      <Layout>
        <Layout.Header
          style={{
            background: '#fff',
            borderBottom: '1px solid #f0f0f0',
            padding: isMobile ? '0 12px' : '0 24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: isMobile ? 8 : 16,
            height: 56,
            lineHeight: '56px',
          }}
        >
          {isMobile ? (
            <Space size={4}>
              <Button
                type="text"
                icon={<MenuOutlined />}
                aria-label="menu"
                onClick={() => setNavOpen(true)}
              />
              <Typography.Text strong>{t('app.title')}</Typography.Text>
            </Space>
          ) : (
            <span />
          )}
          <Space size={isMobile ? 8 : 16}>
            <Select<Locale>
              size="small"
              value={i18n.language as Locale}
              onChange={setLocale}
              options={LOCALES.map((l) => ({ value: l, label: l.toUpperCase() }))}
              style={{ width: 76 }}
            />
            <Dropdown
              menu={{
                items: [
                  {
                    key: 'logout',
                    icon: <LogoutOutlined />,
                    label: t('app.logout'),
                    onClick: () => {
                      clear();
                      void navigate('/login', { replace: true });
                    },
                  },
                ],
              }}
            >
              <Space style={{ cursor: 'pointer' }}>
                <Typography.Text
                  strong
                  ellipsis
                  style={isMobile ? { maxWidth: 120 } : undefined}
                >
                  {user?.displayName}
                </Typography.Text>
                {!isMobile && <Typography.Text type="secondary">{user?.role}</Typography.Text>}
              </Space>
            </Dropdown>
          </Space>
        </Layout.Header>

        <Layout.Content style={{ padding: isMobile ? 12 : 24 }}>
          {/* Lazy route chunks suspend here, keeping the chrome on screen. */}
          <Suspense
            fallback={
              <div style={{ display: 'grid', placeItems: 'center', minHeight: '60vh' }}>
                <Spin size="large" />
              </div>
            }
          >
            <Outlet />
          </Suspense>
        </Layout.Content>
      </Layout>
    </Layout>
  );
}
